if (!customElements.get('sa-sticky-nav')) {
  class SaStickyNav extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.initTargets = this.initTargets.bind(this);
      this.calculateTopOffset = this.calculateTopOffset.bind(this);
    }

    connectedCallback() {
      this.items = this.querySelectorAll('.sa-sticky-nav__item');
      if (this.items.length === 0) return;

      // Initial calculation
      this.calculateTopOffset();
      
      // Bind click events
      this.items.forEach(item => {
        item.addEventListener('click', this.handleClick);
      });

      // Initialize scroll observation
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.calculateTopOffset);
      
      // Initialize targets later to ensure all sections are loaded
      this.initTargets();
      
      // Re-initialize targets after a short delay to ensure DOM is ready
      if (document.readyState === 'loading') {
         document.addEventListener('DOMContentLoaded', () => {
             this.initTargets();
             this.handleScroll();
         });
      } else {
         // Even if readyState is complete, give a small tick for other scripts
         setTimeout(() => {
             this.initTargets();
             this.handleScroll();
         }, 100);
      }
    }

    initTargets() {
      this.targets = Array.from(this.items).map(item => {
        const id = item.getAttribute('data-target-id');
        if (!id) return null;
        
        // Handle both ID (#) and Class (.) selectors if provided, default to ID
        const selector = id.startsWith('#') || id.startsWith('.') ? id : `#${id}`;
        
        // Try to find element
        let element = document.querySelector(selector);
        
        return {
          link: item,
          id: id,
          selector: selector,
          element: element
        };
      }).filter(target => target && target.element);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.calculateTopOffset);
      this.items.forEach(item => {
        item.removeEventListener('click', this.handleClick);
      });
    }

    calculateTopOffset() {
      // 1. Get Sticky Header Height
      let headerHeight = 0;
      const header = document.querySelector('.shopify-section--header') || document.querySelector('header');
      
      if (header) {
        const headerStyle = getComputedStyle(header);
        const isFixed = headerStyle.position === 'fixed' || headerStyle.position === 'sticky';
        const rootStyles = getComputedStyle(document.documentElement);
        const isConfigSticky = rootStyles.getPropertyValue('--sticky-header-enabled').trim() === '1';
        
        if (isFixed || isConfigSticky) {
           headerHeight = header.getBoundingClientRect().height;
        }
      }

      // 2. Get Announcement Bar Height if sticky
      let announcementBarHeight = 0;
      const announcementBar = document.querySelector('.shopify-section--announcement-bar');
      
      if (announcementBar) {
         const barStyle = getComputedStyle(announcementBar);
         const isFixed = barStyle.position === 'fixed' || barStyle.position === 'sticky';
         const rootStyles = getComputedStyle(document.documentElement);
         const isConfigSticky = rootStyles.getPropertyValue('--sticky-announcement-bar-enabled').trim() === '1';
         
         if (isFixed || isConfigSticky) {
            announcementBarHeight = announcementBar.getBoundingClientRect().height;
         }
      }

      const totalTop = headerHeight + announcementBarHeight;
      
      // Set CSS variable on the component or section
      this.style.setProperty('--sa-nav-top', `${totalTop}px`);
      
      const sectionWrapper = this.closest('.shopify-section');
      if (sectionWrapper) {
        sectionWrapper.style.setProperty('--sa-nav-top', `${totalTop}px`);
      }

      // Also expose global variables for other sections to consume
      const navHeight = this.getBoundingClientRect().height || this.offsetHeight || 0;
      const root = document.documentElement;
      if (root) {
        root.style.setProperty('--sa-nav-top', `${totalTop}px`);
        root.style.setProperty('--sa-nav-height', `${navHeight}px`);
        root.style.setProperty('--sa-nav-offset-bottom', `${totalTop + navHeight}px`);
      }
    }

    getOffset() {
      const topStr = this.style.getPropertyValue('--sa-nav-top') || getComputedStyle(this).getPropertyValue('--sa-nav-top');
      const top = parseInt(topStr) || 0;
      // Add a small buffer (10px) to ensure content isn't flush against the nav
      return top + this.offsetHeight + 10;
    }

    handleClick(e) {
      e.preventDefault();
      const targetId = e.currentTarget.getAttribute('data-target-id');
      
      // Try to find in cache first
      let targetObj = this.targets ? this.targets.find(t => t.id === targetId) : null;

      // If not in cache or cache invalid, try fresh lookup
      if (!targetObj || !targetObj.element) {
         const selector = targetId.startsWith('#') || targetId.startsWith('.') ? targetId : `#${targetId}`;
         const element = document.querySelector(selector);
         if (element) {
            targetObj = {
                link: e.currentTarget,
                id: targetId,
                selector: selector,
                element: element
            };
            // Update cache if possible
            if (this.targets) {
                this.targets.push(targetObj);
            }
         }
      }

      if (targetObj && targetObj.element) {
        const offset = this.getOffset();
        // Recalculate element position to be safe
        const elementPosition = targetObj.element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
        
        // Update active state manually for immediate feedback
        this.updateActiveState(targetObj.link);
      }
    }

    handleScroll() {
      if (!this.targets || this.targets.length === 0) return;

      const scrollPosition = window.scrollY;
      const offset = this.getOffset(); // Use offset to determine "active" area
      
      // Find the current active section
      // We want the section that is closest to the top of the viewport (largest top value <= current scroll)
      let currentSection = null;
      let maxTop = -Infinity;

      for (const target of this.targets) {
        const elementTop = target.element.getBoundingClientRect().top + window.scrollY;
        // If we scrolled past this section's top (minus offset)
        if (scrollPosition >= elementTop - offset - 50) { // 50px buffer
           // If this section starts further down (closer to current view) than previous match
           // OR if it's the first match
           if (elementTop > maxTop) {
             maxTop = elementTop;
             currentSection = target;
           }
        }
      }

      if (currentSection) {
        if (this.currentActiveItem !== currentSection.link) {
           this.updateActiveState(currentSection.link);
        }
      } else {
        if (this.currentActiveItem) {
           this.clearActiveState();
        }
      }
    }

    updateActiveState(activeItem) {
      this.currentActiveItem = activeItem;
      this.items.forEach(item => {
        if (item === activeItem) {
          item.classList.add('sa-sticky-nav__item--active');
          // Scroll nav container to keep active item in view on mobile
          this.scrollNavToView(item);
        } else {
          item.classList.remove('sa-sticky-nav__item--active');
        }
      });
    }
    
    clearActiveState() {
      this.currentActiveItem = null;
      this.items.forEach(item => {
        item.classList.remove('sa-sticky-nav__item--active');
      });
    }

    scrollNavToView(activeItem) {
      const navContainer = this; // Since the component itself has overflow-x
      const itemLeft = activeItem.offsetLeft;
      const itemWidth = activeItem.offsetWidth;
      const containerWidth = navContainer.clientWidth;
      
      // Center the item
      const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);
      
      navContainer.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }

  customElements.define('sa-sticky-nav', SaStickyNav);
}
